/**
 * YouTube Embed Extractor
 *
 * Purpose:
 * - Extract a YouTube video id from common URL formats.
 * - Keep parsing logic centralized under sns/platforms.
 */

import { Patterns } from "../../constants/patterns";

/**
 * Extract YouTube video ID from URL.
 * Supports:
 * - https://www.youtube.com/watch?v=<id>
 * - https://youtu.be/<id>
 * - https://www.youtube.com/shorts/<id>
 * - https://www.youtube.com/embed/<id>
 */
export function extractYouTubeVideoId(input: string): string | null {
    try {
        const raw = String(input || "").trim();
        if (!raw) {
            return null;
        }

        const match = raw.match(Patterns.YOUTUBE_ID);
        if (match && match[1]) {
            return match[1];
        }

        let parsedUrl: URL;
        try {
            parsedUrl = new URL(raw);
        } catch {
            return null;
        }

        const host = (parsedUrl.hostname || "").toLowerCase();
        const path = parsedUrl.pathname || "";

        if (host === "youtu.be") {
            const id = path.split("/").filter(Boolean)[0] || "";
            return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
        }

        const videoId = parsedUrl.searchParams.get("v") || "";
        if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
            return videoId;
        }

        const parts = path.split("/").filter(Boolean);
        const shortsIndex = parts.indexOf("shorts");
        if (shortsIndex >= 0 && parts[shortsIndex + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[shortsIndex + 1])) {
            return parts[shortsIndex + 1];
        }

        const embedIndex = parts.indexOf("embed");
        if (embedIndex >= 0 && parts[embedIndex + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[embedIndex + 1])) {
            return parts[embedIndex + 1];
        }

        return null;
    } catch (error) {
        console.error("extractYouTubeVideoId failed", error);
        return null;
    }
}
